import React from 'react';
import Editor from '@monaco-editor/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { webContainerService } from '../../services/webcontainer.service';
import { streamProject, buildProject, getTurnStatus, attachTurn } from '../../services/projectStream.service';
import { projectFilesService } from '../../services/projectFiles.service';
import { FileTree } from './FileTree';
import { FileSearch } from './FileSearch';
import { MediaPanel } from './MediaPanel';
import { AnalyticsPanel } from './AnalyticsPanel';
import { FONT_MONO } from '../../views/landing/landingData';
import { useConfirm } from '../common/ConfirmDialog';
import { useToast } from '../common/Toast';
import {
    BrandBtn, DangerNote, Eyebrow, Icons, NothingHere, PreviewSkeleton, RoundBtn, SUGGESTIONS, Spinner, Star, Tip,
    fmtCredits, fmtDuration, type Theme,
} from './studioUi';

/**
 * Code-first studio (Lovable-style) — the primary builder surface.
 *
 * Layout: chat panel on the LEFT (conversation + live progress steps); the
 * RIGHT pane follows the header tab — "Preview" shows the WebContainer live
 * preview framed to the selected viewport, "Code" shows file tree + Monaco +
 * the raw output terminal.
 *
 * Flow: boot WebContainer + the app's git-backed workspace -> npm install +
 * dev -> live iframe. AI streams files (SSE) which are written into the VFS;
 * Vite HMR patches the preview. User edits are debounced into the same VFS AND
 * persisted to the server workspace (each save becomes a git commit), so user
 * edits and AI edits share one source of truth.
 */
interface HistoryRow {
    id: string;
    role: 'USER' | 'ASSISTANT' | 'SYSTEM';
    content: string;
    createdAt?: string;
    /** ASSISTANT rows: the turn's step list, recorded server-side. */
    metadata?: { steps?: { content: string; at: number }[]; doneAt?: number } | null;
}

interface CodeStudioProps {
    applicationId: string;
    pageId?: string;
    /** When set (Dashboard "describe your app" flow), auto-runs one generation on boot. */
    initialPrompt?: string;
    /** Header tab: which right-pane surface is visible. */
    activeTab: 'preview' | 'code' | 'media' | 'analytics';
    /** Header viewport switcher: how wide the preview iframe is framed. */
    viewport: 'desktop' | 'tablet' | 'mobile';
    /** Studio theme — picks the Monaco theme to match. */
    theme: Theme;
    /** Persisted conversation (ChatHistory rows) to seed the chat panel. */
    initialHistory?: HistoryRow[];
    /** Remaining credits, shown next to the chat input. */
    credits?: number;
    /** Called when a turn ends so the parent can re-read the authoritative balance. */
    onCreditsMaybeChanged?: () => void;
    /** Opens the top-up dialog (credits button in the composer). */
    onOpenTopUp?: () => void;
}

/** Ordered bubble content: model narrative reads as prose; commands/file ops
 *  render as the design's checked step rows. `at` timestamps each entry so the
 *  card can show how long every step took. */
interface ChatSegment {
    kind: 'text' | 'step';
    content: string;
    at?: number;
}

interface ChatMsg {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    segments?: ChatSegment[];
    working?: boolean;
    error?: boolean;
    /** When the turn finished (closes the timing of the last step). */
    doneAt?: number;
}

type Status = 'idle' | 'booting' | 'installing' | 'ready' | 'error';

/** Files worth opening first, in order of preference (Astro scaffold, then React). */
const PREFERRED_FILES = ['src/pages/index.astro', 'src/App.tsx', 'src/pages/index.tsx', 'index.html'];
const DEFAULT_FILE = PREFERRED_FILES[0];
/** First file to show: the first preferred path present, else the first source file, else anything. */
const pickInitialFile = (list: string[]): string | undefined =>
    PREFERRED_FILES.find((f) => list.includes(f)) ?? list.find((f) => f.startsWith('src/')) ?? list[0];

/** Preview frame max-width per header device selection (the design's 980/760/400). */
const FRAME_WIDTH: Record<CodeStudioProps['viewport'], string> = {
    desktop: '980px',
    tablet: '760px',
    mobile: '400px',
};

/** Markdown renderer for assistant cards, styled compactly for the 352px chat panel. */
const ChatMarkdown: React.FC<{ text: string; error?: boolean }> = ({ text, error }) => {
    const ink = error ? 'var(--hv-danger)' : 'var(--hv-ink)';
    const body = error ? 'var(--hv-danger)' : 'var(--hv-body)';
    const codeStyle: React.CSSProperties = { borderRadius: 6, background: 'var(--hv-page-2)', padding: '1px 5px', fontFamily: FONT_MONO, fontSize: 11, color: 'var(--hv-mint-ink)' };
    return (
        <div className="hv-md" style={{ fontSize: 13.5, lineHeight: 1.65, color: body, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                    p: ({ children }) => <p style={{ margin: 0 }}>{children}</p>,
                    strong: ({ children }) => <strong style={{ fontWeight: 700, color: ink }}>{children}</strong>,
                    em: ({ children }) => <em>{children}</em>,
                    a: ({ href, children }) => (
                        <a href={href} target="_blank" rel="noreferrer" className="hv-h-underline" style={{ color: 'var(--hv-mint-ink)' }}>{children}</a>
                    ),
                    ul: ({ children }) => <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4 }}>{children}</ul>,
                    ol: ({ children }) => <ol style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4 }}>{children}</ol>,
                    li: ({ children }) => <li>{children}</li>,
                    h1: ({ children }) => <p style={{ margin: 0, fontWeight: 700, color: ink }}>{children}</p>,
                    h2: ({ children }) => <p style={{ margin: 0, fontWeight: 700, color: ink }}>{children}</p>,
                    h3: ({ children }) => <p style={{ margin: 0, fontWeight: 700, color: ink }}>{children}</p>,
                    h4: ({ children }) => <p style={{ margin: 0, fontWeight: 700, color: ink }}>{children}</p>,
                    code: ({ children }) => <code style={codeStyle}>{children}</code>,
                    pre: ({ children }) => (
                        <pre style={{ margin: 0, overflowX: 'auto', borderRadius: 10, background: 'var(--hv-page-2)', border: '1px solid var(--hv-line)', padding: 10, fontFamily: FONT_MONO, fontSize: 11, lineHeight: 1.5 }}>{children}</pre>
                    ),
                    table: ({ children }) => (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>{children}</table>
                        </div>
                    ),
                    th: ({ children }) => <th style={{ borderBottom: '1px solid var(--hv-line-2)', padding: '4px 8px', textAlign: 'left', fontWeight: 700, color: ink }}>{children}</th>,
                    td: ({ children }) => <td style={{ borderBottom: '1px solid var(--hv-line)', padding: '4px 8px', verticalAlign: 'top' }}>{children}</td>,
                    hr: () => <hr style={{ border: 0, borderTop: '1px solid var(--hv-line)', margin: '4px 0' }} />,
                    blockquote: ({ children }) => (
                        <blockquote style={{ margin: 0, borderLeft: '2px solid var(--hv-leaf)', paddingLeft: 10, color: 'var(--hv-muted)' }}>{children}</blockquote>
                    ),
                }}
            >
                {text}
            </ReactMarkdown>
        </div>
    );
};

/**
 * Make raw process output readable in a <pre>: strip ANSI escape sequences
 * (colors, cursor moves like npm's `ESC[1G ESC[0K` spinner) and emulate
 * carriage-return overwrites (keep only the final content of each line).
 */
function sanitizeTerminalText(text: string): string {
    return (
        text
            // OSC sequences (e.g. terminal title): ESC ] ... BEL / ESC \
            .replace(/\x1b\][^\x07\x1b]*(?:\x07|\x1b\\)/g, '')
            // "Move to column 1" acts like a carriage return
            .replace(/\x1b\[1G/g, '\r')
            // All other CSI sequences (colors, clear-line, cursor moves)
            .replace(/\x1b\[[0-9;?]*[A-Za-z]/g, '')
            // Spinner overwrites: keep only what's after the last CR on each line
            .split('\n')
            .map((line) => (line.includes('\r') ? line.slice(line.lastIndexOf('\r') + 1) : line))
            .join('\n')
    );
}

function languageFor(path: string): string {
    if (path.endsWith('.tsx') || path.endsWith('.ts')) return 'typescript';
    if (path.endsWith('.jsx') || path.endsWith('.js') || path.endsWith('.cjs') || path.endsWith('.mjs')) return 'javascript';
    if (path.endsWith('.css')) return 'css';
    if (path.endsWith('.json')) return 'json';
    if (path.endsWith('.html')) return 'html';
    if (path.endsWith('.md')) return 'markdown';
    return 'plaintext';
}

/**
 * Monaco language setup. The editor has no node_modules in the browser, so
 * semantic validation would flood every file with false errors ("Cannot use
 * JSX unless --jsx", "Cannot find module 'react'"). Enable JSX + keep syntax
 * checking, drop semantic checking — real errors surface via Vite HMR and the
 * server-side verification loop.
 */
function configureMonaco(monaco: any) {
    for (const defaults of [
        monaco.languages.typescript.typescriptDefaults,
        monaco.languages.typescript.javascriptDefaults,
    ]) {
        defaults.setCompilerOptions({
            jsx: monaco.languages.typescript.JsxEmit.ReactJSX,
            target: monaco.languages.typescript.ScriptTarget.ES2020,
            module: monaco.languages.typescript.ModuleKind.ESNext,
            moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
            allowNonTsExtensions: true,
            allowJs: true,
            esModuleInterop: true,
            skipLibCheck: true,
        });
        defaults.setDiagnosticsOptions({
            noSemanticValidation: true,
            noSyntaxValidation: false,
        });
    }
}

export const CodeStudio: React.FC<CodeStudioProps> = ({
    applicationId,
    pageId,
    initialPrompt,
    activeTab,
    viewport,
    theme,
    initialHistory = [],
    credits,
    onCreditsMaybeChanged,
    onOpenTopUp,
}) => {
    const [status, setStatus] = React.useState<Status>('idle');
    // Fallback tanpa WebContainer. Safari tidak mendukung COEP `credentialless`
    // (nilai yang dikirim /studio), jadi halaman tidak pernah cross-origin
    // isolated di sana. Chat/editor tetap bekerja langsung terhadap workspace
    // server; preview memakai build server-side (handleBuild) alih-alih HMR.
    const [fallbackMode, setFallbackMode] = React.useState(false);
    const fallbackRef = React.useRef(false);
    // Cache file workspace di sisi klien saat fallback (pengganti VFS container).
    const serverFilesRef = React.useRef<Record<string, string>>({});
    const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
    const [files, setFiles] = React.useState<string[]>([]);
    const [activeFile, setActiveFile] = React.useState<string>(DEFAULT_FILE);
    const [code, setCode] = React.useState<string>('');
    const [logs, setLogs] = React.useState<string>('');
    const [prompt, setPrompt] = React.useState('');
    const [isStreaming, setIsStreaming] = React.useState(false);
    const [isBuilding, setIsBuilding] = React.useState(false);
    const [buildUrl, setBuildUrl] = React.useState<string | null>(null);
    // Monaco instance + a parked "scroll to this line" request from file search.
    const editorRef = React.useRef<any>(null);
    const pendingReveal = React.useRef<{ path: string; line: number } | null>(null);
    const [error, setError] = React.useState<string | null>(null);
    const confirm = useConfirm();
    const notify = useToast();
    const [dirtyPaths, setDirtyPaths] = React.useState<Set<string>>(new Set());
    const [previewPath, setPreviewPath] = React.useState('/'); // route shown in the iframe
    const [pathInput, setPathInput] = React.useState('/'); // editable address-bar value
    const [reloadKey, setReloadKey] = React.useState(0); // bump to force-reload the iframe
    // First-build overlay over the preview: shown until the AI writes its first
    // file (then the user watches the app assemble live via HMR).
    const [buildOverlay, setBuildOverlay] = React.useState(false);
    const [latestStep, setLatestStep] = React.useState('');
    // In-app fullscreen preview — reliable alternative to opening the
    // WebContainer URL in a new tab (which needs a popup handshake back to us).
    const [isFullscreen, setIsFullscreen] = React.useState(false);
    // Resizable panels (Code tab): Output height + preview column width.
    const [outputHeight, setOutputHeight] = React.useState(() =>
        Math.min(600, Math.max(80, parseInt(localStorage.getItem('hyvay:outputHeight') || '160', 10) || 160)),
    );
    const [previewWidth, setPreviewWidth] = React.useState(() =>
        Math.min(900, Math.max(260, parseInt(localStorage.getItem('hyvay:previewWidth') || '360', 10) || 360)),
    );
    const [isResizing, setIsResizing] = React.useState(false);
    const dragRef = React.useRef<{ axis: 'x' | 'y'; startPos: number; startVal: number } | null>(null);

    React.useEffect(() => {
        const onMove = (e: PointerEvent) => {
            const d = dragRef.current;
            if (!d) return;
            if (d.axis === 'y') {
                const h = Math.min(600, Math.max(80, d.startVal + (d.startPos - e.clientY)));
                setOutputHeight(h);
                localStorage.setItem('hyvay:outputHeight', String(h));
            } else {
                const w = Math.min(900, Math.max(260, d.startVal + (d.startPos - e.clientX)));
                setPreviewWidth(w);
                localStorage.setItem('hyvay:previewWidth', String(w));
            }
        };
        const onUp = () => {
            if (!dragRef.current) return;
            dragRef.current = null;
            setIsResizing(false);
            document.body.style.userSelect = '';
            document.body.style.cursor = '';
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        return () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };
    }, []);

    const startResize = (axis: 'x' | 'y') => (e: React.PointerEvent) => {
        dragRef.current = { axis, startPos: axis === 'y' ? e.clientY : e.clientX, startVal: axis === 'y' ? outputHeight : previewWidth };
        setIsResizing(true);
        document.body.style.userSelect = 'none';
        document.body.style.cursor = axis === 'y' ? 'row-resize' : 'col-resize';
    };
    // Collapsible chat panel (collapses to a thin rail).
    const [chatCollapsed, setChatCollapsed] = React.useState(false);
    // "Fix it" loop: generated apps postMessage runtime errors to the studio.
    const [previewError, setPreviewError] = React.useState<{ message: string; stack?: string } | null>(null);
    // Toast for turn-rejected-because-busy (satu build per user), auto-dismiss.
    const [toast, setToast] = React.useState<string | null>(null);
    React.useEffect(() => {
        if (!toast) return;
        const t = setTimeout(() => setToast(null), 5000);
        return () => clearTimeout(t);
    }, [toast]);
    // Collapsible file tree in the Code view.
    const [filesCollapsed, setFilesCollapsed] = React.useState(false);

    React.useEffect(() => {
        if (!isFullscreen) return;
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setIsFullscreen(false);
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isFullscreen]);
    const [messages, setMessages] = React.useState<ChatMsg[]>(() =>
        initialHistory
            .filter((m) => m.role === 'USER' || m.role === 'ASSISTANT')
            .map((m) => ({
                id: m.id,
                role: m.role === 'USER' ? ('user' as const) : ('assistant' as const),
                content: m.content,
                // Restore the step card (collapsed) so a reload doesn't lose it.
                segments: m.metadata?.steps?.map((st) => ({ kind: 'step' as const, content: st.content, at: st.at })),
                doneAt: m.metadata?.doneAt,
            })),
    );
    /** Step cards the user expanded; every card starts collapsed to one line. */
    const [expandedCards, setExpandedCards] = React.useState<Set<string>>(() => new Set());
    const toggleCard = (id: string) =>
        setExpandedCards((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    const writeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const dirty = React.useRef<{ path: string; content: string } | null>(null);
    const activeFileRef = React.useRef(activeFile);
    activeFileRef.current = activeFile;
    const chatEndRef = React.useRef<HTMLDivElement | null>(null);
    const isStreamingRef = React.useRef(isStreaming);
    isStreamingRef.current = isStreaming;
    const messagesRef = React.useRef(messages);
    messagesRef.current = messages;

    // Runtime errors reported by the generated app (scaffold main.tsx posts
    // 'hyvay:runtime-error' from window.onerror / unhandledrejection / the
    // ErrorBoundary). Ignored while the agent streams — HMR churn mid-edit
    // produces transient errors that resolve themselves.
    React.useEffect(() => {
        const onMessage = (e: MessageEvent) => {
            const d = e.data;
            if (!d || typeof d.type !== 'string') return;
            // Reflect in-app navigation in the address bar (display only — never
            // touch previewPath, which would reload the iframe).
            if (d.type === 'hyvay:navigate' && typeof d.path === 'string') {
                setPathInput(d.path);
                return;
            }
            if (d.type !== 'hyvay:runtime-error' || typeof d.message !== 'string') return;
            if (isStreamingRef.current) return;
            setPreviewError({
                message: d.message.slice(0, 400),
                stack: typeof d.stack === 'string' ? d.stack.slice(0, 2000) : undefined,
            });
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, []);

    const appendLog = React.useCallback((line: string) => {
        // Sanitize the whole buffer (not just the chunk) so CR-overwrites that
        // span chunk boundaries still collapse correctly.
        setLogs((prev) => sanitizeTerminalText(prev + line).slice(-20000)); // cap buffer
    }, []);

    // Auto-scroll the chat as messages/steps stream in — and once more when the
    // turn ends, since the suggestion chips only appear then.
    React.useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isStreaming]);

    const refreshFiles = React.useCallback(async () => {
        if (fallbackRef.current) {
            setFiles(Object.keys(serverFilesRef.current).sort());
            return;
        }
        try {
            setFiles(await webContainerService.listFiles());
        } catch {
            /* container not ready yet */
        }
    }, []);

    /** Read a file from the VFS, or from the server-files cache in fallback mode. */
    const readFileAny = React.useCallback(async (path: string): Promise<string> => {
        if (fallbackRef.current) return serverFilesRef.current[path] ?? '';
        return webContainerService.readFile(path).catch(() => '');
    }, []);

    /** Flush any pending debounced write immediately (e.g. before switching files). */
    const flushWrite = React.useCallback(async () => {
        if (writeTimer.current) {
            clearTimeout(writeTimer.current);
            writeTimer.current = null;
        }
        const pending = dirty.current;
        dirty.current = null;
        if (pending) {
            try {
                // Write to the WebContainer (HMR) and persist to the server workspace.
                // Fallback mode has no container — the server workspace IS the state.
                if (fallbackRef.current) serverFilesRef.current[pending.path] = pending.content;
                else await webContainerService.writeFile(pending.path, pending.content);
                await projectFilesService.write(applicationId, pending.path, pending.content);
                // Flushed — no longer dirty.
                setDirtyPaths((prev) => {
                    if (!prev.has(pending.path)) return prev;
                    const next = new Set(prev);
                    next.delete(pending.path);
                    return next;
                });
            } catch (e: any) {
                appendLog(`\n[write error] ${e.message}\n`);
            }
        }
    }, [appendLog, applicationId]);

    // Flush pending edits when leaving the studio (component unmount), best-effort.
    React.useEffect(() => {
        return () => {
            void flushWrite();
        };
    }, [flushWrite]);

    // Boot the container + dev server. The service is idempotent, so StrictMode's
    // double-invoke (dev) is harmless — no teardown on cleanup.
    React.useEffect(() => {
        // Server-side studio: no WebContainer. The agent runs server-side, the editor
        // reads/writes the server workspace, and the preview comes from a server build.
        const enterFallback = async () => {
            fallbackRef.current = true;
            setFallbackMode(true);
            const serverFiles = await projectFilesService.readAll(applicationId);
            serverFilesRef.current = serverFiles;
            const list = Object.keys(serverFiles).sort();
            setFiles(list);
            const initial = pickInitialFile(list);
            if (initial) {
                setActiveFile(initial);
                setCode(serverFiles[initial] ?? '');
            }
            setStatus('ready');
            // Here the preview IS a server build, so start one immediately instead of
            // parking a "build it" placeholder in the pane. Skipped when a turn is
            // about to run: that path builds on completion anyway.
            if (list.length > 0 && !initialPrompt) void handleBuild();
        };
        // Phones report cross-origin isolation and then still fail: WebContainer's
        // mobile support is beta and the preview runs out of memory, which used to
        // surface as a raw boot error before the studio was usable at all.
        const isMobileBrowser = () =>
            typeof navigator !== 'undefined' &&
            (/Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) ||
                (navigator.maxTouchPoints > 1 && /Macintosh/i.test(navigator.userAgent)));
        (async () => {
            try {
                if (!webContainerService.isSupported) {
                    // Client-side navigation from a non-isolated page (e.g. the
                    // dashboard at "/") keeps the old document. One hard reload at
                    // this /studio URL picks up the COOP/COEP headers. Guarded so a
                    // misconfigured server can't cause a reload loop.
                    const guard = 'hyvay:isolation-reload';
                    if (!sessionStorage.getItem(guard)) {
                        sessionStorage.setItem(guard, '1');
                        window.location.reload();
                        return;
                    }
                    // Still not isolated after the reload: the BROWSER can't do it
                    // (Safari ignores COEP `credentialless`).
                    await enterFallback();
                    return;
                }
                if (isMobileBrowser()) {
                    await enterFallback();
                    return;
                }
                sessionStorage.removeItem('hyvay:isolation-reload');
                setStatus('booting');
                // The container is a page singleton: if a DIFFERENT app is still
                // mounted (SPA navigation home -> another project), tear it down
                // first or this project would show the previous project's content.
                webContainerService.prepareFor(applicationId);

                // Source of truth is the server workspace. Boot the container and
                // fetch the workspace CONCURRENTLY (neither needs the other), then
                // mount once per app; StrictMode double-mounts of the SAME app
                // reuse the container.
                const needMount = !webContainerService.hasMountedFor(applicationId);
                // One retry: for a brand-new app the first prompt's turn may be
                // creating the workspace at this very moment.
                const readWorkspace = async () => {
                    try {
                        return await projectFilesService.readAll(applicationId);
                    } catch {
                        await new Promise((r) => setTimeout(r, 1500));
                        return projectFilesService.readAll(applicationId);
                    }
                };
                const [, serverFiles] = await Promise.all([
                    webContainerService.boot(),
                    needMount ? readWorkspace() : Promise.resolve(null),
                ]);
                if (needMount && serverFiles) {
                    await webContainerService.mount(serverFiles, applicationId, appendLog);
                }
                const list = await webContainerService.listFiles();
                setFiles(list);
                const initial = pickInitialFile(list);
                if (initial) {
                    setActiveFile(initial);
                    setCode(await webContainerService.readFile(initial).catch(() => ''));
                }

                setStatus('installing');
                const url = await webContainerService.start(appendLog);
                setPreviewUrl(url);
                setStatus('ready');
            } catch (e: any) {
                // A failed boot must not dead-end the studio (memory limits, blocked
                // service workers, third-party-cookie rules): degrade to the
                // server-side path, and only report if even that fails.
                try {
                    await enterFallback();
                } catch {
                    setError(e?.message || 'Failed to start WebContainer');
                    setStatus('error');
                }
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Switch the active file, flushing unsaved edits first so nothing is lost.
    const selectFile = async (path: string) => {
        if (path === activeFile) return;
        await flushWrite();
        setActiveFile(path);
        setCode(await readFileAny(path));
    };

    /**
     * Open a file from a search hit, optionally scrolled to a line.
     *
     * The reveal cannot happen inline: Monaco swaps its model when the `path`/`value`
     * props change on the next render, so the request is parked and applied by the
     * effect below once the editor is actually showing that file.
     */
    const openFileAt = async (path: string, line?: number) => {
        if (line) pendingReveal.current = { path, line };
        await selectFile(path);
    };

    React.useEffect(() => {
        const want = pendingReveal.current;
        const ed = editorRef.current;
        if (!want || !ed || want.path !== activeFile) return;
        pendingReveal.current = null;
        ed.revealLineInCenter(want.line);
        ed.setPosition({ lineNumber: want.line, column: 1 });
        ed.focus();
    }, [activeFile, code]);

    // Debounced write of editor edits into the VFS (HMR picks it up).
    const onEditorChange = (value?: string) => {
        const next = value ?? '';
        setCode(next);
        dirty.current = { path: activeFile, content: next };
        setDirtyPaths((prev) => (prev.has(activeFile) ? prev : new Set(prev).add(activeFile)));
        if (writeTimer.current) clearTimeout(writeTimer.current);
        writeTimer.current = setTimeout(() => {
            void flushWrite();
        }, 300);
    };

    const handleNewFile = async () => {
        const path = window.prompt('New file path (e.g. src/components/Hero.tsx)')?.trim();
        if (!path) return;
        if (files.includes(path)) {
            await selectFile(path);
            return;
        }
        if (fallbackRef.current) serverFilesRef.current[path] = '';
        else await webContainerService.writeFile(path, '').catch((e) => appendLog(`\n[write error] ${e.message}\n`));
        await projectFilesService.write(applicationId, path, '').catch((e) => appendLog(`\n[persist error] ${e.message}\n`));
        await refreshFiles();
        await selectFile(path);
    };

    const clearDirty = (path: string) =>
        setDirtyPaths((prev) => {
            if (!prev.has(path)) return prev;
            const next = new Set(prev);
            next.delete(path);
            return next;
        });

    const handleRename = async (path: string) => {
        const target = window.prompt('Rename file to:', path)?.trim();
        if (!target || target === path) return;
        if (files.includes(target)) {
            appendLog(`\n[rename] ${target} already exists\n`);
            return;
        }
        await flushWrite(); // persist latest edits before copying
        try {
            if (fallbackRef.current) {
                serverFilesRef.current[target] = serverFilesRef.current[path] ?? '';
                delete serverFilesRef.current[path];
            } else {
                await webContainerService.renameFile(path, target);
            }
            await projectFilesService.rename(applicationId, path, target);
        } catch (e: any) {
            appendLog(`\n[rename error] ${e.message}\n`);
            return;
        }
        clearDirty(path);
        await refreshFiles();
        if (path === activeFile) {
            setActiveFile(target);
            setCode(await readFileAny(target));
        }
    };

    const handleDelete = async (path: string) => {
        const ok = await confirm({
            title: <>Delete <em>{path}</em>?</>,
            description: 'The file is removed from the workspace and the change is committed to the project history.',
            confirmLabel: 'Delete file',
            onConfirm: async () => {
                try {
                    if (fallbackRef.current) delete serverFilesRef.current[path];
                    else await webContainerService.deleteFile(path);
                    await projectFilesService.remove(applicationId, path);
                } catch (e: any) {
                    appendLog(`\n[delete error] ${e.message}\n`);
                    throw new Error(e?.message || 'Could not delete the file.');
                }
            },
        });
        if (!ok) return;
        notify.success(`${path} deleted.`);
        clearDirty(path);
        const remaining = fallbackRef.current
            ? Object.keys(serverFilesRef.current).sort()
            : await webContainerService.listFiles().catch(() => [] as string[]);
        setFiles(remaining);
        if (path === activeFile) {
            const fallback = pickInitialFile(remaining);
            setActiveFile(fallback ?? '');
            setCode(fallback ? await readFileAny(fallback) : '');
        }
    };

    // Best-effort: flush pending edits and warn the user if they navigate away dirty.
    React.useEffect(() => {
        const handler = (e: BeforeUnloadEvent) => {
            if (dirty.current || dirtyPaths.size > 0) {
                void flushWrite();
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [dirtyPaths, flushWrite]);

    /** Append a segment to the working assistant message (steps deduped). */
    const pushSegment = (id: string, kind: ChatSegment['kind'], content: string) => {
        if (kind === 'step') setLatestStep(content);
        setMessages((prev) =>
            prev.map((m) => {
                if (m.id !== id) return m;
                const segments = m.segments ?? [];
                const last = segments[segments.length - 1];
                if (kind === 'step' && last?.kind === 'step' && last.content === content) return m;
                return { ...m, segments: [...segments.slice(-80), { kind, content, at: Date.now() }] };
            }),
        );
    };

    /** Derive a chat-worthy step from a raw log line; null = terminal-only noise. */
    const stepFromLog = (line: string): string | null => {
        const text = line.trim();
        if (text.startsWith('$ ')) return text;
        if (text.startsWith('===')) return text.replace(/=/g, '').trim();
        if (text.startsWith('✓') || text.startsWith('·')) return text;
        if (text.startsWith('[verify]') || text.startsWith('[agent error]')) return text;
        return null;
    };

    const handleGenerate = async (overrideText?: string, reuseWorkId?: string) => {
        const text = (overrideText ?? prompt).trim();
        if (!text || isStreaming) return;
        setIsStreaming(true);
        setError(null);
        setPrompt('');
        setPreviewError(null); // a new turn may fix it; the app re-reports if it persists
        appendLog(`\n> ${text}\n`);

        // Show the building overlay only for the app's FIRST build (no completed
        // assistant turns yet) — later edits shouldn't hide the working app.
        const isFirstBuild = !messages.some((m) => m.role === 'assistant' && !m.working);
        if (isFirstBuild && !fallbackRef.current) {
            // Fallback mode keeps its own preview panel visible (server-side build
            // status) instead of the live-HMR overlay.
            setLatestStep('');
            setBuildOverlay(true);
        }

        const workId = reuseWorkId ?? `work-${Date.now()}`;
        const userMsgId = reuseWorkId ? 'user-boot' : `user-${Date.now()}`;
        setMessages((prev) =>
            reuseWorkId
                ? prev.map((m) => (m.id === reuseWorkId ? { ...m, segments: [], working: true } : m))
                : [
                      ...prev,
                      { id: userMsgId, role: 'user', content: text },
                      { id: workId, role: 'assistant', content: '', segments: [], working: true },
                  ],
        );
        // First user gesture is the moment to ask for notification permission
        // (used to announce completion when the tab is in the background).
        if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
            void Notification.requestPermission();
        }

        // followTurn starts the turn and transparently reconnects if the SSE
        // stream drops while the background job keeps running server-side.
        await followTurn(workId, text, { userMsgId, promptText: text });
    };

    /** Notify when a turn finishes and the user is looking elsewhere. */
    const notifyDone = (ok: boolean) => {
        if (typeof Notification === 'undefined' || Notification.permission !== 'granted' || !document.hidden) return;
        try {
            new Notification('HyVay Studio', {
                body: ok ? 'Your build is ready ✓' : 'The build stopped because of an error',
            });
        } catch {
            /* unsupported */
        }
    };

    /** Context for a rejected-before-start turn (one build per user). */
    interface BusyCtx {
        userMsgId: string;
        promptText: string;
    }

    /** Shared callbacks for live turns AND background-turn reattach. */
    const makeStreamCallbacks = (workId: string, busyCtx?: BusyCtx) => {
        const finish = (patch: Partial<ChatMsg>) =>
            setMessages((prev) => prev.map((m) => (m.id === workId ? { ...m, working: false, doneAt: m.doneAt ?? Date.now(), ...patch } : m)));
        return {
            onLog: (line: string) => {
                appendLog(line);
                const step = stepFromLog(line);
                if (step) pushSegment(workId, 'step', step);
            },
            onReasoning: (text: string) => {
                appendLog(`\n· ${text}\n`);
                pushSegment(workId, 'text', text);
            },
            onFile: async (path: string, contents: string) => {
                pushSegment(workId, 'step', `✎ ${path}`);
                // The first-build skeleton stays up until the turn ends (design
                // behaviour): the half-written site and the scaffold placeholder
                // are never shown mid-build. Later turns have no overlay at all.
                if (fallbackRef.current) serverFilesRef.current[path] = contents;
                else await webContainerService.writeFile(path, contents);
                await refreshFiles();
                if (path === activeFileRef.current) setCode(contents); // reflect in editor if open
            },
            onDelete: async (path: string) => {
                pushSegment(workId, 'step', `✖ ${path}`);
                if (fallbackRef.current) delete serverFilesRef.current[path];
                else await webContainerService.deleteFile(path).catch(() => undefined);
                await refreshFiles();
            },
            onRename: async (from: string, to: string) => {
                pushSegment(workId, 'step', `↪ ${from} → ${to}`);
                if (fallbackRef.current) {
                    serverFilesRef.current[to] = serverFilesRef.current[from] ?? '';
                    delete serverFilesRef.current[from];
                } else {
                    await webContainerService.renameFile(from, to).catch(() => undefined);
                }
                await refreshFiles();
                if (from === activeFileRef.current) {
                    setActiveFile(to);
                    setCode(await readFileAny(to));
                }
            },
            onMessage: (message: string) => {
                appendLog(`\n${message}\n`);
                finish({ content: message });
            },
            onDone: () => {
                setIsStreaming(false);
                setBuildOverlay(false);
                notifyDone(true);
                onCreditsMaybeChanged?.();
                // No HMR without a container — refresh the server-built preview so
                // the fallback browser still sees the result of every turn.
                if (fallbackRef.current) void handleBuild();
            },
            onError: (msg: string) => {
                setError(msg);
                finish({ content: msg || 'Something went wrong. Please try again.', error: true });
                setIsStreaming(false);
                setBuildOverlay(false);
                notifyDone(false);
                // A failed turn is not charged, but re-read anyway: the failure may
                // have come AFTER the deduction (e.g. a later verify round).
                onCreditsMaybeChanged?.();
            },
            // The turn never started (user already has a build running): toast it,
            // take the pending bubbles back out of the chat and give the prompt
            // back to the input so nothing is lost.
            onBusy: busyCtx
                ? (msg: string) => {
                      setMessages((prev) => prev.filter((m) => m.id !== workId && m.id !== busyCtx.userMsgId));
                      setPrompt(busyCtx.promptText);
                      setToast(msg);
                      setIsStreaming(false);
                      setBuildOverlay(false);
                  }
                : undefined,
        };
    };

    /**
     * Follow a turn to completion, surviving SSE drops. The turn runs as a
     * background job, so a dropped stream is NOT a failure — we reconnect via
     * attachTurn (which replays from the server's event log) as long as the turn
     * is still running. Only a real server 'done'/'error' event ends the follow.
     *
     * @param startPrompt when set, this is a NEW turn (enqueue via streamProject);
     *        omit to just follow an already-running turn (reattach).
     */
    const followTurn = async (workId: string, startPrompt?: string, busyCtx?: BusyCtx) => {
        const base = makeStreamCallbacks(workId, busyCtx);
        let terminal = false;
        const cbs = {
            ...base,
            onDone: () => {
                terminal = true;
                base.onDone();
            },
            onError: (m: string) => {
                terminal = true;
                base.onError(m);
            },
            // 'busy' is terminal too — without this the reconnect loop would see
            // "no turn running" and reconcile as a successful done.
            onBusy: base.onBusy
                ? (m: string) => {
                      terminal = true;
                      base.onBusy!(m);
                  }
                : undefined,
        };

        const MAX_RECONNECTS = 60;
        for (let attempt = 0; attempt <= MAX_RECONNECTS; attempt++) {
            try {
                if (attempt === 0 && startPrompt !== undefined) {
                    await streamProject({ applicationId, pageId, prompt: startPrompt }, cbs);
                } else {
                    await attachTurn(applicationId, cbs);
                }
            } catch {
                /* transport drop — decide via turn status below */
            }
            if (terminal) return;

            const st = await getTurnStatus(applicationId).catch(() => ({ running: false }));
            if (!st.running) {
                // Turn finished while we were disconnected: one more attach replays
                // the full event log (incl. the terminal event we missed).
                try {
                    await attachTurn(applicationId, cbs);
                } catch {
                    /* ignore */
                }
                if (!terminal) base.onDone(); // reconcile: nothing more to wait for
                return;
            }
            // Still running — reconnect after a short backoff.
            pushSegment(workId, 'step', 'Connection lost — reconnecting…');
            await new Promise((r) => setTimeout(r, Math.min(2000 + attempt * 1000, 8000)));
        }
    };

    // Background-turn reattach: if a generation is still running server-side
    // (user closed/reloaded the tab mid-turn), reconnect once the container is
    // ready — the server replays all past events, then follows live.
    const reattachTried = React.useRef(false);
    React.useEffect(() => {
        if (status !== 'ready' || reattachTried.current) return;
        reattachTried.current = true;
        void (async () => {
            const turn = await getTurnStatus(applicationId);
            if (!turn.running || isStreamingRef.current) return;
            setIsStreaming(true);
            // Reloaded mid FIRST build: keep the skeleton up like a fresh start would.
            if (!fallbackRef.current && !messagesRef.current.some((m) => m.role === 'assistant' && !m.working)) {
                setLatestStep('');
                setBuildOverlay(true);
            }
            const workId = 'work-reattach';
            setMessages((prev) => {
                // The running turn's USER row is already persisted to ChatHistory,
                // so on re-entry it arrives via initialHistory too — adding it here
                // again showed the prompt twice. Only add when it's not already the
                // last user bubble. (turn.prompt is truncated to 300 chars server-side.)
                const lastUser = [...prev].reverse().find((m) => m.role === 'user');
                const isDup = !!turn.prompt && lastUser?.content.slice(0, 300) === turn.prompt;
                return [
                ...prev,
                ...(turn.prompt && !isDup ? [{ id: 'user-reattach', role: 'user' as const, content: turn.prompt }] : []),
                {
                    id: workId,
                    role: 'assistant' as const,
                    content: '',
                    segments: [{ kind: 'step' as const, content: 'Reconnecting to the build that is still running…', at: Date.now() }],
                    working: true,
                },
                ];
            });
            try {
                await followTurn(workId); // reconnects across drops until the turn ends
            } finally {
                setIsStreaming(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, applicationId]);

    // Dashboard hand-off: the prompt runs IMMEDIATELY. The agent works
    // server-side against the git workspace and never needed the browser
    // preview — waiting for the WebContainer to boot + install (measured 50-60 s
    // cold) before sending the prompt was pure serialization. The preview boots
    // in parallel; file events that arrive before it is mounted are queued by
    // webContainerService and replayed after mount, so nothing is lost. The
    // preview pane shows its own "Booting/Installing" overlay meanwhile.
    const initialRan = React.useRef(false);
    const bootWorkId = React.useRef<string | null>(null);
    // Decided at RENDER time (before the boot effect sets its guard): when the
    // page is not cross-origin isolated yet, the boot effect is about to reload
    // it — starting the turn now would enqueue it twice (the prompt survives the
    // reload in sessionStorage and starts then).
    const reloadImminent = React.useRef(
        !webContainerService.isSupported && !sessionStorage.getItem('hyvay:isolation-reload'),
    );
    React.useEffect(() => {
        if (reloadImminent.current) return;
        if (initialPrompt && !initialRan.current && !bootWorkId.current) {
            bootWorkId.current = 'work-boot';
            initialRan.current = true;
            setMessages((prev) => [
                ...prev,
                { id: 'user-boot', role: 'user', content: initialPrompt },
                { id: 'work-boot', role: 'assistant', content: '', segments: [{ kind: 'step', content: 'Sending your prompt to the agent…', at: Date.now() }], working: true },
            ]);
            void handleGenerate(initialPrompt, bootWorkId.current);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialPrompt]);
    // A preview that fails to start must not look like a failed generation:
    // the turn keeps running server-side, so only surface it in the terminal.
    React.useEffect(() => {
        if (status === 'error' && error) appendLog(`\n[preview] ${error}\n`);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    const handleBuild = async () => {
        if (isBuilding) return;
        await flushWrite(); // make sure latest edits are persisted before building
        setIsBuilding(true);
        setError(null);
        appendLog(`\n=== Building (npm install + vite build) — first build may take a minute ===\n`);
        try {
            await buildProject(applicationId, {
                onLog: (line) => appendLog(line),
                onDone: (url) => {
                    setBuildUrl(url);
                    appendLog(`\n🚀 Build ready: ${url}\n`);
                    setIsBuilding(false);
                    if (fallbackRef.current) {
                        // The built dist IS the preview here — show it in place.
                        setPreviewUrl(url);
                        setReloadKey((k) => k + 1);
                    } else {
                        window.open(url, '_blank', 'noreferrer');
                    }
                },
                onError: (msg) => {
                    setError(msg);
                    appendLog(`\n[build failed] ${msg}\n`);
                    setIsBuilding(false);
                },
            });
        } catch (e: any) {
            setError(e?.message || 'Build failed');
            setIsBuilding(false);
        }
    };

    const normalizedPath = previewPath.startsWith('/') ? previewPath : `/${previewPath}`;
    const iframeSrc = previewUrl ? `${previewUrl.replace(/\/$/, '')}${normalizedPath}` : '';
    // A chat turn costs 1 credit, so below 1 there is nothing to spend.
    const outOfCredits = typeof credits === 'number' && credits < 1;
    const canSend = prompt.trim().length > 0 && !isStreaming && status !== 'error' && !outOfCredits;

    const navigatePreview = (e: React.FormEvent) => {
        e.preventDefault();
        setPreviewPath(pathInput.trim() || '/');
        setReloadKey((k) => k + 1);
    };

    const lastAssistantId = [...messages].reverse().find((m) => m.role === 'assistant')?.id;

    /** Elapsed time of a step: until the next timestamped segment, or until the
     *  turn ended. Sub-quarter-second steps show nothing — a wall of "0.0s" is noise. */
    const stepTime = (m: ChatMsg, i: number): string | null => {
        const segs = m.segments ?? [];
        const at = segs[i]?.at;
        if (!at) return null;
        const next = segs.slice(i + 1).find((s) => s.at)?.at ?? m.doneAt;
        if (!next || next - at < 250) return null;
        return fmtDuration(next - at);
    };

    /** The row already carries a check icon, so drop the log's own tick/bullet prefix. */
    const stepLabel = (text: string) => text.replace(/^[✓·]\s*/, '');

    /** Collapsed line of a finished turn: "Done · 10 files changed · 1m 12s". */
    const turnSummary = (m: ChatMsg): string => {
        const segs = m.segments ?? [];
        // Lockfile / astro's env.d.ts are rewritten by npm and the build, not by the agent.
        const files = new Set(
            segs
                .filter((s) => s.kind === 'step' && /^[✎✖↪] /.test(s.content))
                .map((s) => s.content.slice(2))
                .filter((f) => f !== 'package-lock.json' && f !== 'src/env.d.ts'),
        );
        const start = segs.find((s) => s.at)?.at;
        return [
            m.error ? 'Stopped' : 'Done',
            files.size ? `${files.size} file${files.size === 1 ? '' : 's'} changed` : null,
            start && m.doneAt ? fmtDuration(m.doneAt - start) : null,
        ]
            .filter(Boolean)
            .join(' · ');
    };

    // Status line under the skeleton — honest about what the wait is.
    const skeletonLabel = fallbackMode
        ? (isBuilding ? 'BUILDING THE PREVIEW ON THE SERVER…' : undefined)
        : status === 'booting' ? 'BOOTING THE PREVIEW…'
        : status === 'installing' ? 'INSTALLING DEPENDENCIES…'
        : status === 'idle' ? 'STARTING…'
        : buildOverlay ? 'BUILDING YOUR SITE…'
        : undefined;

    /* ---------------------------------------------------------------- styles */
    const cardStyle: React.CSSProperties = { alignSelf: 'flex-start', width: '100%', padding: 14, borderRadius: '18px 18px 18px 6px', background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 18px 34px -34px var(--hv-shadow)' };
    const chipStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', height: 30, padding: '0 12px', border: '1px solid var(--hv-line-2)', borderRadius: 999, background: 'transparent', color: 'var(--hv-body)', fontSize: 12, fontWeight: 600, cursor: 'pointer' };
    const paneHead: React.CSSProperties = { flex: 'none', display: 'flex', alignItems: 'center', gap: 8, height: 52, padding: '0 16px', borderBottom: '1px solid var(--hv-line)', background: 'var(--hv-page)' };
    const smallIconBtn: React.CSSProperties = { width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 0, padding: 0, background: 'transparent', color: 'var(--hv-muted)', cursor: 'pointer', borderRadius: 8 };
    const subHead: React.CSSProperties = { flex: 'none', display: 'flex', alignItems: 'center', gap: 8, height: 36, padding: '0 12px', borderBottom: '1px solid var(--hv-line)', background: 'var(--hv-page)' };

    /* ------------------------------------------------------------ preview UI */
    // Friendly and honest: the agent works fine; only LIVE preview needs a browser
    // with cross-origin isolation support (Safari lacks it).
    const fallbackPreviewPanel = (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, background: 'var(--hv-card)', padding: 24, textAlign: 'center' }}>
            <span style={{ width: 54, height: 54, borderRadius: 999, background: 'var(--hv-mint)', color: 'var(--hv-mint-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{Icons.desktop({ size: 22 })}</span>
            <div style={{ maxWidth: 420 }}>
                <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--hv-ink)' }}>Preview is built on the server</div>
                <p style={{ margin: '6px 0 0', fontSize: 12.5, lineHeight: 1.6, color: 'var(--hv-body)' }}>
                    This browser cannot run the in-app preview, so HyVay builds your site on the server and shows it here — automatically after every turn. Chat and the editor work exactly as they do on desktop.
                </p>
            </div>
            <BrandBtn onClick={() => void handleBuild()} disabled={isBuilding || isStreaming} style={{ height: 40, padding: '0 16px', fontSize: 13 }}>
                {isBuilding ? <><Spinner size={14} light /> Building the preview…</> : <>{Icons.reload({})} Rebuild preview</>}
            </BrandBtn>
        </div>
    );

    const bootErrorPanel = (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, background: 'var(--hv-card)', padding: 24, textAlign: 'center' }}>
            <DangerNote style={{ maxWidth: 440 }}>{error}</DangerNote>
            {!webContainerService.isSupported && (
                <BrandBtn onClick={() => window.location.reload()} style={{ height: 40, padding: '0 16px', fontSize: 13 }}>{Icons.reload({})} Reload to enable the preview</BrandBtn>
            )}
        </div>
    );

    // Single frame element for every tab/fullscreen state — only its styles change,
    // so the iframe never remounts and the dev server keeps its state.
    const framed = activeTab === 'preview' || isFullscreen;

    return (
        <div className="hv-st-body" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', color: 'var(--hv-ink)' }}>
            {/* Busy toast: the turn was rejected because a build is already running */}
            {toast && (
                <div role="status" style={{ position: 'fixed', top: 18, left: '50%', transform: 'translateX(-50%)', zIndex: 120, display: 'flex', alignItems: 'center', gap: 9, padding: '10px 14px', borderRadius: 12, background: '#24262A', color: '#F3F2EC', fontSize: 13, fontWeight: 600, boxShadow: '0 24px 44px -24px rgba(0,0,0,0.6)', animation: 'hvFade 0.25s ease both' }}>
                    <Spinner size={14} light /> {toast}
                </div>
            )}

            {/* ---- Left: chat panel (collapsible to a 48px rail) ---------------- */}
            {chatCollapsed ? (
                <div style={{ flex: 'none', width: 48, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, padding: '13px 0', borderRight: '1px solid var(--hv-line)', background: 'var(--hv-page)' }}>
                    <Tip label="Expand" align="left">
                        <button type="button" onClick={() => setChatCollapsed(false)} aria-label="Expand chat" className="hv-h-brand-ink" style={smallIconBtn}>{Icons.expand({})}</button>
                    </Tip>
                    <span style={{ display: 'flex', opacity: isStreaming ? 1 : 0.35, animation: isStreaming ? 'hvPulse 1.4s ease-in-out infinite' : undefined }}><Star size={14} /></span>
                </div>
            ) : (
                <div className="hv-st-chat" style={{ flex: 'none', width: 352, display: 'flex', flexDirection: 'column', minHeight: 0, borderRight: '1px solid var(--hv-line)', background: 'var(--hv-page)' }}>
                    <div style={paneHead}>
                        <Star size={14} />
                        <Eyebrow>CHAT</Eyebrow>
                        <div style={{ flex: 1 }} />
                        <Tip label="Collapse" align="right">
                            <button type="button" onClick={() => setChatCollapsed(true)} aria-label="Collapse chat" className="hv-h-brand-ink" style={smallIconBtn}>{Icons.collapse({})}</button>
                        </Tip>
                    </div>

                    <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {messages.length === 0 && (
                            <div style={{ ...cardStyle, fontSize: 13.5, lineHeight: 1.65, color: 'var(--hv-body)' }}>
                                Describe what you want to build. HyVay edits your site's real code and the preview updates live.
                            </div>
                        )}
                        {messages.map((m) => {
                            if (m.role === 'user') {
                                return (
                                    <div key={m.id} style={{ alignSelf: 'flex-end', maxWidth: '86%', padding: '12px 14px', borderRadius: '18px 18px 6px 18px', background: 'var(--hv-mint)', color: 'var(--hv-mint-ink)', fontSize: 13.5, lineHeight: 1.6, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                                        {m.content}
                                    </div>
                                );
                            }
                            const segs = m.segments ?? [];
                            const showChips = m.id === lastAssistantId && !isStreaming && !m.error && !!m.content;
                            return (
                                <React.Fragment key={m.id}>
                                    {(segs.length > 0 || (m.working && !m.content)) && (
                                        <div style={cardStyle}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <Star size={13} />
                                                <Eyebrow style={{ letterSpacing: '0.14em' }}>HYVAY</Eyebrow>
                                            </div>
                                            {(() => {
                                                // Collapsed: one line — the step in progress while working,
                                                // the summary once done. The chevron opens the full list.
                                                const open = expandedCards.has(m.id);
                                                const current = [...segs].reverse().find((s) => s.kind === 'step');
                                                return (
                                                    <button type="button" onClick={() => toggleCard(m.id)} aria-expanded={open} style={{ marginTop: 12, width: '100%', display: 'flex', alignItems: 'center', gap: 9, padding: 0, border: 0, background: 'transparent', cursor: 'pointer', textAlign: 'left', fontSize: 13, fontWeight: 600, color: 'var(--hv-ink)' }}>
                                                        {m.working ? (
                                                            <Spinner size={17} />
                                                        ) : (
                                                            <span style={{ flex: 'none', width: 17, height: 17, borderRadius: 999, background: m.error ? 'var(--hv-danger-bg)' : 'var(--hv-mint)', color: 'var(--hv-mint-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{Icons.check({})}</span>
                                                        )}
                                                        <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                            {m.working ? (current ? stepLabel(current.content) : 'Working…') : turnSummary(m)}
                                                        </span>
                                                        <span style={{ flex: 'none', display: 'flex', color: 'var(--hv-muted)' }}>{open ? Icons.chevronD({}) : Icons.chevronR({})}</span>
                                                    </button>
                                                );
                                            })()}
                                            {expandedCards.has(m.id) && (
                                            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--hv-line)', display: 'flex', flexDirection: 'column', gap: 9 }}>
                                                {segs.map((s, i) => {
                                                    const isLast = i === segs.length - 1;
                                                    const active = isLast && m.working;
                                                    if (s.kind === 'text') {
                                                        return (
                                                            <p key={i} style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: 'var(--hv-body)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', animation: 'hvFade 0.3s ease both' }}>{s.content}</p>
                                                        );
                                                    }
                                                    const time = active ? null : stepTime(m, i);
                                                    return (
                                                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, fontWeight: active ? 600 : 400, color: active ? 'var(--hv-ink)' : 'var(--hv-body)', animation: 'hvFade 0.3s ease both' }}>
                                                            {active ? (
                                                                <Spinner size={17} />
                                                            ) : (
                                                                <span style={{ flex: 'none', width: 17, height: 17, borderRadius: 999, background: 'var(--hv-mint)', color: 'var(--hv-mint-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{Icons.check({})}</span>
                                                            )}
                                                            <span title={s.content} style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{stepLabel(s.content)}</span>
                                                            {time && <span style={{ flex: 'none', fontFamily: FONT_MONO, fontSize: 9.5, color: 'var(--hv-muted)' }}>{time}</span>}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            )}
                                        </div>
                                    )}
                                    {m.content && (
                                        <div style={{ ...cardStyle, animation: 'hvRise 0.5s cubic-bezier(0.22,0.9,0.24,1) both', ...(m.error ? { borderColor: 'var(--hv-danger-bg)', background: 'var(--hv-danger-bg)' } : {}) }}>
                                            <ChatMarkdown text={m.content} error={m.error} />
                                            {showChips && (
                                                <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                                                    {SUGGESTIONS.map((label) => (
                                                        <button key={label} type="button" onClick={() => setPrompt(label)} className="hv-h-mint-bd" style={chipStyle}>{label}</button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </React.Fragment>
                            );
                        })}

                        {/* "Fix it": runtime error reported by the preview */}
                        {previewError && (
                            <div style={{ padding: '14px 16px', borderRadius: 14, background: 'var(--hv-danger-bg)', color: 'var(--hv-danger)', display: 'flex', flexDirection: 'column', gap: 10 }}>
                                <div style={{ fontSize: 13, fontWeight: 700 }}>Error detected in the preview</div>
                                <p style={{ margin: 0, maxHeight: 64, overflow: 'hidden', fontFamily: FONT_MONO, fontSize: 11, lineHeight: 1.5, opacity: 0.9 }}>{previewError.message}</p>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <button
                                        type="button"
                                        disabled={isStreaming}
                                        onClick={() => {
                                            const err = previewError;
                                            if (!err) return;
                                            void handleGenerate(
                                                `Fix the following runtime error that appears in the app preview. Find and fix the ROOT CAUSE with a minimal change:\n\n${err.message}\n\n${err.stack ?? ''}`,
                                            );
                                        }}
                                        style={{ height: 32, padding: '0 12px', border: 0, borderRadius: 999, background: 'var(--hv-danger)', color: '#FFFDF7', fontSize: 12, fontWeight: 700, cursor: isStreaming ? 'not-allowed' : 'pointer', opacity: isStreaming ? 0.6 : 1 }}
                                    >
                                        Fix this error
                                    </button>
                                    <button type="button" onClick={() => setPreviewError(null)} className="hv-h-underline" style={{ height: 32, padding: '0 10px', border: 0, background: 'transparent', color: 'var(--hv-danger)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                        Dismiss
                                    </button>
                                </div>
                            </div>
                        )}
                        <div ref={chatEndRef} />
                    </div>

                    <div style={{ flex: 'none', padding: '14px 16px 16px' }}>
                        <div className="hv-composer" style={{ padding: 12, borderRadius: 18, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 20px 40px -36px var(--hv-shadow)', transition: 'border-color 0.18s ease, box-shadow 0.18s ease' }}>
                            <textarea
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        if (canSend) void handleGenerate();
                                    }
                                }}
                                rows={2}
                                placeholder={outOfCredits ? 'Out of credits — top up to continue' : 'Describe a change — e.g. make the hero photo full-bleed'}
                                style={{ width: '100%', border: 0, outline: 'none', resize: 'none', background: 'transparent', color: 'var(--hv-ink)', fontSize: 13.5, lineHeight: 1.6, display: 'block' }}
                            />
                            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <button
                                    type="button"
                                    onClick={onOpenTopUp}
                                    title="Each message uses 1 credit"
                                    className="hv-h-brand-bd-card2"
                                    style={{ display: 'flex', alignItems: 'center', gap: 9, height: 36, padding: '0 13px', borderRadius: 999, background: outOfCredits ? 'var(--hv-danger-bg)' : 'transparent', border: `1px solid ${outOfCredits ? 'transparent' : 'var(--hv-line)'}`, cursor: 'pointer' }}
                                >
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700, color: outOfCredits ? 'var(--hv-danger)' : 'var(--hv-mint-ink)' }}>
                                        {Icons.coins({})}
                                        {fmtCredits(credits)} credits
                                    </span>
                                </button>
                                <div style={{ flex: 1 }} />
                                <button
                                    type="button"
                                    data-track="studio_send_message"
                                    onClick={() => void handleGenerate()}
                                    disabled={!canSend}
                                    aria-label="Send message"
                                    title={outOfCredits ? 'Not enough credits (1 credit per message)' : 'Send'}
                                    style={{ width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 0, borderRadius: 999, background: canSend ? 'var(--brand,#13422C)' : 'var(--hv-page-2)', color: canSend ? '#FFFDF7' : 'var(--hv-muted)', cursor: canSend ? 'pointer' : 'not-allowed' }}
                                >
                                    {isStreaming ? <Spinner size={15} /> : Icons.up({})}
                                </button>
                            </div>
                        </div>
                        {outOfCredits && (
                            <p style={{ margin: '8px 2px 0', fontSize: 11.5, lineHeight: 1.5, color: 'var(--hv-danger)', fontWeight: 600 }}>
                                You are out of credits. Each message uses 1 credit — top up to keep building.
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* ---- Right: address bar + tab surface --------------------------------- */}
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', position: 'relative', background: 'var(--hv-page-2)' }}>
                <div aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.7, backgroundImage: 'radial-gradient(circle,var(--hv-line-2) 1.2px,transparent 1.8px)', backgroundSize: '24px 24px' }} />

                {/* Address bar — reload / route / fullscreen / open in new tab */}
                <form onSubmit={navigatePreview} style={{ ...paneHead, position: 'relative', zIndex: 2, margin: 0 }}>
                    <Tip label="Reload preview" align="left">
                        <RoundBtn size={30} filled disabled={!previewUrl} onClick={() => setReloadKey((k) => k + 1)} aria-label="Reload preview" style={previewUrl ? undefined : { opacity: 0.45, cursor: 'not-allowed' }}>{Icons.reload({})}</RoundBtn>
                    </Tip>
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 9, height: 32, padding: '0 13px', borderRadius: 999, background: 'var(--hv-card)', border: '1px solid var(--hv-line)' }}>
                        {/* <span style={{ flex: 'none', fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: '0.06em', color: 'var(--hv-muted)', whiteSpace: 'nowrap' }}>preview</span> */}
                        <input
                            value={pathInput}
                            onChange={(e) => setPathInput(e.target.value)}
                            disabled={!previewUrl}
                            spellCheck={false}
                            placeholder="/"
                            aria-label="Preview route"
                            style={{ flex: 1, minWidth: 0, border: 0, outline: 'none', background: 'transparent', fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: '0.06em', color: 'var(--hv-body)', opacity: previewUrl ? 1 : 0.5 }}
                        />
                    </div>
                    <Tip label={isFullscreen ? 'Exit fullscreen (Esc)' : 'Fullscreen preview'}>
                        <RoundBtn size={30} filled disabled={!previewUrl} onClick={() => setIsFullscreen((f) => !f)} aria-label="Fullscreen preview" style={previewUrl ? undefined : { opacity: 0.45, cursor: 'not-allowed' }}>{isFullscreen ? Icons.exitFullscreen({}) : Icons.fullscreen({})}</RoundBtn>
                    </Tip>
                    {/* <Tip label="Open in new tab" align="right">
                        <a
                            href={iframeSrc || undefined}
                            target="_blank"
                            rel="noreferrer"
                            aria-label="Open in new tab"
                            title="The preview lives in THIS tab, so the new tab reconnects through a popup — allow it when the browser asks. Publish to get a public URL."
                            className="hv-h-mint-bd"
                            style={{ width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--hv-line)', borderRadius: 999, background: 'var(--hv-card)', color: 'var(--hv-body)', ...(previewUrl ? {} : { pointerEvents: 'none', opacity: 0.45 }) }}
                        >
                            {Icons.external({})}
                        </a>
                    </Tip> */}
                </form>

                {/* Progress rail while a turn streams */}
                {isStreaming && (
                    <div style={{ position: 'relative', zIndex: 3, flex: 'none', height: 2, background: 'var(--hv-line)', overflow: 'hidden' }}>
                        <div style={{ width: '100%', height: '100%', background: 'var(--hv-brand-ink)', animation: 'hvRail 1.1s cubic-bezier(0.5,0.05,0.4,0.95) infinite' }} />
                    </div>
                )}

                <div style={{ position: 'relative', zIndex: 2, flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' }}>
                    {/* Media tab — mounted fresh each open so the library reloads */}
                    {activeTab === 'media' && <MediaPanel applicationId={applicationId} />}
                    {activeTab === 'analytics' && <AnalyticsPanel applicationId={applicationId} />}

                    {/* Code tab: file tree + Monaco + output. Stays MOUNTED across tab
                        switches (hidden via CSS) so Monaco keeps its state. */}
                    <div style={{ flex: 1, minWidth: 0, display: activeTab === 'code' ? 'flex' : 'none', flexDirection: 'column', background: 'var(--hv-page)', borderRight: isFullscreen ? undefined : '1px solid var(--hv-line)' }}>
                        {files.length === 0 ? (
                            <NothingHere
                                icon={Icons.code({ size: 22 })}
                                hint={status === 'ready' || fallbackMode ? 'Your files appear here as soon as the first build writes them.' : undefined}
                            />
                        ) : (
                            <>
                                <div style={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' }}>
                                    {filesCollapsed ? (
                                        <div style={{ flex: 'none', width: 40, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '8px 0', borderRight: '1px solid var(--hv-line)', background: 'var(--hv-page)' }}>
                                            <Tip label="Show files" align="left">
                                                <button type="button" onClick={() => setFilesCollapsed(false)} aria-label="Show files" className="hv-h-brand-ink" style={smallIconBtn}>{Icons.expand({ size: 17 })}</button>
                                            </Tip>
                                        </div>
                                    ) : (
                                        <div style={{ flex: 'none', width: 232, display: 'flex', flexDirection: 'column', minHeight: 0, borderRight: '1px solid var(--hv-line)', background: 'var(--hv-page)' }}>
                                            <div style={subHead}>
                                                <Eyebrow>FILES</Eyebrow>
                                                <div style={{ flex: 1 }} />
                                                <Tip label="New file">
                                                    <button type="button" onClick={() => void handleNewFile()} aria-label="New file" className="hv-h-brand-ink" style={{ ...smallIconBtn, width: 24, height: 24 }}>{Icons.plus({})}</button>
                                                </Tip>
                                                <Tip label="Hide files" align="right">
                                                    <button type="button" onClick={() => setFilesCollapsed(true)} aria-label="Hide files" className="hv-h-brand-ink" style={{ ...smallIconBtn, width: 24, height: 24 }}>{Icons.collapse({ size: 16 })}</button>
                                                </Tip>
                                            </div>
                                            <FileSearch files={files} readFile={readFileAny} canSearchContent={status === 'ready'} onOpen={openFileAt}>
                                                <FileTree files={files} activeFile={activeFile} dirtyPaths={dirtyPaths} onSelect={selectFile} onRename={handleRename} onDelete={handleDelete} />
                                            </FileSearch>
                                        </div>
                                    )}

                                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', background: 'var(--hv-card)' }}>
                                        <div style={{ ...subHead, background: 'var(--hv-card)', gap: 8 }}>
                                            <span style={{ display: 'flex', color: 'var(--hv-muted)' }}>{Icons.file({})}</span>
                                            <span style={{ flex: 1, minWidth: 0, fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: '0.04em', color: 'var(--hv-body)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{activeFile}</span>
                                            {dirtyPaths.has(activeFile) && <Eyebrow style={{ color: 'var(--hv-clay)' }}>SAVING…</Eyebrow>}
                                        </div>
                                        <div style={{ flex: 1, minHeight: 0 }}>
                                            <Editor
                                                height="100%"
                                                theme={theme === 'dark' ? 'vs-dark' : 'vs'}
                                                language={languageFor(activeFile)}
                                                path={activeFile}
                                                value={code}
                                                beforeMount={configureMonaco}
                                                onMount={(editor) => {
                                                    editorRef.current = editor;
                                                }}
                                                onChange={onEditorChange}
                                                options={{ minimap: { enabled: false }, fontSize: 13, scrollBeyondLastLine: false, fontFamily: "'Space Mono', Menlo, monospace", padding: { top: 12 } }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                {/* Output terminal (drag the handle to resize) */}
                                <div
                                    onPointerDown={startResize('y')}
                                    title="Drag to resize output"
                                    className="hv-h-brand-bg"
                                    style={{ flex: 'none', height: 5, cursor: 'row-resize', background: 'var(--hv-line)', transition: 'background 0.15s ease' }}
                                />
                                <div style={{ flex: 'none', height: outputHeight, display: 'flex', flexDirection: 'column', background: 'var(--hv-card-2)' }}>
                                    <div style={{ ...subHead, height: 32, background: 'var(--hv-card-2)' }}>
                                        <span style={{ display: 'flex', color: 'var(--hv-muted)' }}>{Icons.terminal({})}</span>
                                        <Eyebrow>OUTPUT</Eyebrow>
                                        <div style={{ flex: 1 }} />
                                        {buildUrl && (
                                            <a href={buildUrl} target="_blank" rel="noreferrer" className="hv-h-underline" style={{ fontSize: 12, fontWeight: 700, color: 'var(--hv-mint-ink)' }}>View build ↗</a>
                                        )}
                                    </div>
                                    <pre style={{ flex: 1, minHeight: 0, margin: 0, overflow: 'auto', padding: '8px 12px', fontFamily: FONT_MONO, fontSize: 11, lineHeight: 1.6, color: 'var(--hv-body)', whiteSpace: 'pre-wrap' }}>
                                        {logs || 'Waiting…'}
                                    </pre>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Preview-width drag handle (Code tab, beside the editor) */}
                    {activeTab === 'code' && !isFullscreen && files.length > 0 && (
                        <div
                            onPointerDown={startResize('x')}
                            title="Drag to resize preview"
                            className="hv-h-brand-bg"
                            style={{ flex: 'none', width: 5, cursor: 'col-resize', background: 'var(--hv-line)', transition: 'background 0.15s ease' }}
                        />
                    )}

                    {/* Preview column — framed canvas on the Preview tab, a resizable
                        column beside the editor on the Code tab, hidden (but mounted) on
                        Media, fixed overlay when fullscreen. */}
                    <div
                        style={
                            isFullscreen
                                ? { position: 'fixed', inset: 0, zIndex: 60, display: 'flex', background: 'var(--hv-card)' }
                                : activeTab === 'preview'
                                    ? { flex: 1, minWidth: 0, minHeight: 0, overflow: 'auto', padding: 22, display: 'flex', justifyContent: 'center' }
                                    : activeTab === 'code' && files.length > 0
                                        ? { flex: 'none', width: previewWidth, minHeight: 0, display: 'flex', flexDirection: 'column', background: 'var(--hv-page)' }
                                        : { display: 'none' }
                        }
                    >
                        {isFullscreen && (
                            <div style={{ position: 'absolute', top: 14, right: 14, zIndex: 61 }}>
                                <Tip label="Exit fullscreen (Esc)" align="right">
                                    <RoundBtn size={34} filled onClick={() => setIsFullscreen(false)} aria-label="Exit fullscreen">{Icons.exitFullscreen({})}</RoundBtn>
                                </Tip>
                            </div>
                        )}
                        <div style={isFullscreen
                            ? { flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }
                            : framed
                                ? { width: '100%', maxWidth: FRAME_WIDTH[viewport], display: 'flex', flexDirection: 'column', margin: '0 auto', transition: 'max-width 0.3s cubic-bezier(0.2,0.9,0.24,1)' }
                                : { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
                        >
                            <div style={framed && !isFullscreen
                                ? { flex: 1, minHeight: 0, position: 'relative', borderRadius: 16, overflow: 'hidden', background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 40px 64px -40px var(--hv-shadow)' }
                                : { flex: 1, minHeight: 0, position: 'relative', background: 'var(--hv-card)' }}
                            >
                                {previewUrl ? (
                                    <iframe
                                        key={reloadKey}
                                        title="preview"
                                        src={iframeSrc}
                                        /* Chrome 142+ "Local Network Access": a request from a PUBLIC origin
                                           (this iframe is served from webcontainer-api.io) to a loopback
                                           address needs user permission, and inside an iframe that
                                           permission must be DELEGATED or it is denied before CORS even
                                           runs. Only matters for local development — in production
                                           media.<domain> is a public address. Harmless elsewhere. */
                                        allow="local-network-access"
                                        // Iframes swallow pointer events — freeze them while resizing.
                                        style={{ width: '100%', height: '100%', border: 0, display: 'block', pointerEvents: isResizing ? 'none' : 'auto' }}
                                    />
                                ) : fallbackMode ? (
                                    fallbackPreviewPanel
                                ) : status === 'error' ? (
                                    bootErrorPanel
                                ) : (
                                    <PreviewSkeleton label={skeletonLabel} detail={latestStep || undefined} />
                                )}
                                {/* Skeleton over the preview while a build runs: the first build, or any server build in fallback */}
                                {previewUrl && (buildOverlay || (fallbackMode && isBuilding)) && (
                                    <div style={{ position: 'absolute', inset: 0, zIndex: 10 }}>
                                        <PreviewSkeleton label={skeletonLabel} detail={latestStep || undefined} />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
